/**
 * videoEmbed.ts — Normalização de endereços de vídeo do Studio
 *
 * A pessoa cola o endereço que copiou do navegador (YouTube, Vimeo, Loom) ou o
 * endereço direto de um arquivo `.mp4`. Aqui esse endereço vira o formato de
 * incorporação que o navegador sabe tocar, junto com a imagem de capa
 * automática quando o serviço oferece uma.
 *
 * Este arquivo é COMPARTILHADO: o editor (React) e o compilador de HTML usam a
 * mesma leitura, para que a prévia e a página publicada nunca discordem.
 *
 * RELEASE: v1.0.0 - 2026-09-11 - Versão inicial (Studio, categoria Vídeo)
 */

/** Serviço reconhecido a partir do endereço colado. */
export type StudioVideoProvider = 'youtube' | 'vimeo' | 'loom' | 'file' | 'embed' | 'none';

/** Resultado da leitura de um endereço de vídeo. */
export interface StudioVideoSource {
  provider: StudioVideoProvider;
  /** Endereço pronto para o `<iframe>` (vazio quando for arquivo direto). */
  embedUrl: string;
  /** Endereço do arquivo, quando for `.mp4`/`.webm` e não uma incorporação. */
  fileUrl: string;
  /** Capa sugerida pelo serviço (hoje só o YouTube devolve). */
  posterUrl: string;
  /** Endereço original, já limpo. */
  originalUrl: string;
}

const VAZIO: StudioVideoSource = {
  provider: 'none',
  embedUrl: '',
  fileUrl: '',
  posterUrl: '',
  originalUrl: '',
};

/** Só aceitamos endereços http/https — nada de `javascript:` e afins. */
function enderecoSeguro(valor: string): string {
  const limpo = valor.trim();
  if (!limpo) return '';
  if (!/^https?:\/\//i.test(limpo)) return '';
  if (/[<>"']/.test(limpo)) return '';
  return limpo;
}

/** Lê o identificador do vídeo no YouTube em qualquer formato de endereço. */
function idDoYoutube(url: URL): string {
  if (/youtu\.be$/i.test(url.hostname)) return url.pathname.slice(1).split('/')[0] ?? '';
  const caminho = url.pathname.replace(/^\/+/, '');
  if (caminho.startsWith('embed/')) return caminho.slice('embed/'.length).split('/')[0] ?? '';
  if (caminho.startsWith('shorts/')) return caminho.slice('shorts/'.length).split('/')[0] ?? '';
  if (caminho.startsWith('live/')) return caminho.slice('live/'.length).split('/')[0] ?? '';
  return url.searchParams.get('v') ?? '';
}

/** Lê o identificador do vídeo no Vimeo. */
function idDoVimeo(url: URL): string {
  const partes = url.pathname.split('/').filter(Boolean);
  const numero = partes.find((parte) => /^\d+$/.test(parte));
  return numero ?? '';
}

/** Lê o identificador da gravação no Loom. */
function idDoLoom(url: URL): string {
  const partes = url.pathname.split('/').filter(Boolean);
  const indice = partes.findIndex((parte) => parte === 'share' || parte === 'embed');
  return indice >= 0 ? (partes[indice + 1] ?? '') : '';
}

/**
 * Converte o endereço colado pelo usuário no que o navegador precisa.
 * Nunca lança erro: endereço desconhecido devolve `provider: 'none'`.
 */
export function lerVideo(valor: unknown): StudioVideoSource {
  const bruto = enderecoSeguro(String(valor ?? ''));
  if (!bruto) return VAZIO;

  let url: URL;
  try {
    url = new URL(bruto);
  } catch {
    return VAZIO;
  }

  const host = url.hostname.replace(/^www\./i, '').toLowerCase();

  if (host.endsWith('youtube.com') || host === 'youtu.be' || host.endsWith('youtube-nocookie.com')) {
    const id = idDoYoutube(url).replace(/[^\w-]/g, '');
    if (!id) return { ...VAZIO, originalUrl: bruto };
    const inicio = url.searchParams.get('t') ?? url.searchParams.get('start') ?? '';
    const segundos = inicio.replace(/[^0-9]/g, '');
    return {
      provider: 'youtube',
      embedUrl: `https://www.youtube-nocookie.com/embed/${id}?rel=0&autoplay=1${segundos ? `&start=${segundos}` : ''}`,
      fileUrl: '',
      posterUrl: `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`,
      originalUrl: bruto,
    };
  }

  if (host.endsWith('vimeo.com')) {
    const id = idDoVimeo(url);
    if (!id) return { ...VAZIO, originalUrl: bruto };
    return {
      provider: 'vimeo',
      embedUrl: `https://player.vimeo.com/video/${id}?autoplay=1`,
      fileUrl: '',
      posterUrl: '',
      originalUrl: bruto,
    };
  }

  if (host.endsWith('loom.com')) {
    const id = idDoLoom(url);
    if (!id) return { ...VAZIO, originalUrl: bruto };
    return {
      provider: 'loom',
      embedUrl: `https://www.loom.com/embed/${id}?autoplay=1`,
      fileUrl: '',
      posterUrl: '',
      originalUrl: bruto,
    };
  }

  if (/\.(mp4|webm|ogv|ogg|mov)(\?|$)/i.test(url.pathname)) {
    return { provider: 'file', embedUrl: '', fileUrl: bruto, posterUrl: '', originalUrl: bruto };
  }

  // Endereço de incorporação de outro serviço: respeitamos como veio.
  if (/\/embed\//i.test(url.pathname) || /player\./i.test(host)) {
    return { provider: 'embed', embedUrl: bruto, fileUrl: '', posterUrl: '', originalUrl: bruto };
  }

  return { ...VAZIO, originalUrl: bruto };
}
